import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
    X, ExternalLink, Copy, ChevronRight, Send, MessageSquare,
    Phone, Mail, Building2, CheckCircle2, Circle, Clock
} from 'lucide-react';

const STAGE_ORDER = [
    'Committed',
    'Pre-consult Scheduled',
    'Pre-consult Complete',
    'Building',
    'Delivered',
    'Ongoing Support',
];

const STAGE_COLORS = {
    'Committed':             'bg-[#7B61FF]/20 text-[#7B61FF] ring-[#7B61FF]/30',
    'Pre-consult Scheduled': 'bg-blue-500/20 text-blue-400 ring-blue-500/30',
    'Pre-consult Complete':  'bg-teal-500/20 text-teal-400 ring-teal-500/30',
    'Building':              'bg-orange-500/20 text-orange-400 ring-orange-500/30',
    'Delivered':             'bg-green-500/20 text-green-400 ring-green-500/30',
    'Ongoing Support':       'bg-emerald-500/20 text-emerald-400 ring-emerald-500/30',
};

const UPDATE_ICONS = { milestone: '🏆', link: '👀', shipped: '📦', info: 'ℹ️' };

const ClientDrawer = ({
    client,
    onClose,
    onStageChange,
    onToggleStatus,
    onPostUpdate,
    password,
}) => {
    const drawerRef = useRef(null);
    const p = client?.progress || {};
    const intake = client?.intake || {};

    // Close on Escape key
    useEffect(() => {
        const handleKeyDown = (e) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [onClose]);

    // Close on click outside drawer
    const handleBackdropClick = (e) => {
        if (drawerRef.current && !drawerRef.current.contains(e.target)) onClose();
    };

    if (!client) return null;

    const portalUrl = client.portal_url || `https://www.roofclaw.com/onboard/${client.token}`;

    const copyPortalUrl = () => {
        navigator.clipboard.writeText(portalUrl);
    };

    const prereqs = [
        { key: 'intake_complete',       label: 'Intake' },
        { key: 'apple_id_ready',        label: 'Apple ID' },
        { key: 'agent_email_ready',     label: 'Email' },
        { key: 'credentials_submitted', label: 'Creds' },
        { key: 'tailscale_ready',       label: 'Tailscale' },
    ];

    const recentUpdates = (client.client_updates || []).slice(0, 3);

    const handleSubmitUpdate = (e) => {
        e.preventDefault();
        onPostUpdate(e, client.id);
    };

    const stageColorClass = STAGE_COLORS[client.stage] || 'bg-white/5 text-white/40 ring-white/10';

    return (
        <div
            className="fixed inset-0 z-[100] flex justify-end"
            onClick={handleBackdropClick}
        >
            {/* Backdrop */}
            <div className="absolute inset-0 bg-black/60 backdrop-blur-sm animate-fade-in" />

            {/* Drawer */}
            <div
                ref={drawerRef}
                className="relative w-full max-w-md bg-[#0A0A14] border-l border-[#F0EFF4]/10 h-full overflow-y-auto animate-slide-in-right"
            >
                {/* Header */}
                <div className="sticky top-0 z-10 bg-[#0A0A14] border-b border-[#F0EFF4]/10 px-6 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-3 min-w-0">
                        <div className="min-w-0">
                            <Link
                                to={`/admin/client/${client.id}`}
                                className="text-lg font-bold text-[#F0EFF4] hover:text-[#7B61FF] transition-colors truncate block"
                                title="Open full client view"
                            >
                                {client.name || '—'}
                            </Link>
                            <span className="text-xs text-[#F0EFF4]/40">{client.company || '—'}</span>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-1.5 rounded-lg hover:bg-[#F0EFF4]/10 text-[#F0EFF4]/60 hover:text-[#F0EFF4] transition-colors shrink-0"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="px-6 py-5 space-y-6">

                    {/* Contact Info */}
                    <div className="space-y-2">
                        {client.email && (
                            <div className="flex items-center gap-2 text-sm text-[#F0EFF4]/70">
                                <Mail className="w-3.5 h-3.5 text-[#F0EFF4]/40 shrink-0" />
                                <a href={`mailto:${client.email}`} className="hover:text-[#7B61FF] transition-colors truncate">{client.email}</a>
                            </div>
                        )}
                        {client.phone && (
                            <div className="flex items-center gap-2 text-sm text-[#F0EFF4]/70">
                                <Phone className="w-3.5 h-3.5 text-[#F0EFF4]/40 shrink-0" />
                                <a href={`tel:${client.phone}`} className="hover:text-[#7B61FF] transition-colors">{client.phone}</a>
                            </div>
                        )}
                        {client.company && (
                            <div className="flex items-center gap-2 text-sm text-[#F0EFF4]/70">
                                <Building2 className="w-3.5 h-3.5 text-[#F0EFF4]/40 shrink-0" />
                                <span>{client.company}</span>
                            </div>
                        )}
                    </div>

                    {/* Stage */}
                    <div>
                        <div className="text-xs text-[#F0EFF4]/40 uppercase tracking-wider font-data mb-2">Stage</div>
                        <button
                            onClick={() => onStageChange(client.id, client.stage || 'Committed')}
                            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-data font-semibold ring-1 transition-all cursor-pointer hover:scale-105 ${stageColorClass}`}
                        >
                            {client.stage || 'Committed'}
                            <ChevronRight className="w-3 h-3" />
                        </button>
                    </div>

                    {/* Pre-Reqs */}
                    <div>
                        <div className="text-xs text-[#F0EFF4]/40 uppercase tracking-wider font-data mb-2">Prerequisites</div>
                        <div className="flex flex-wrap gap-2">
                            {prereqs.map(({ key, label }) => (
                                <button
                                    key={key}
                                    onClick={() => onToggleStatus(client.id, key, p[key])}
                                    className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-data font-medium transition-all cursor-pointer hover:scale-105 ring-1
                                    ${p[key]
                                        ? 'bg-emerald-500/20 text-emerald-400 ring-emerald-500/30 hover:bg-emerald-500/30'
                                        : 'bg-white/5 text-white/30 ring-white/10 hover:bg-white/10'
                                    }`}
                                >
                                    {p[key]
                                        ? <CheckCircle2 className="w-3 h-3" />
                                        : <Circle className="w-3 h-3" />
                                    }
                                    {label}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Agent Profile (if intake exists) */}
                    {intake.agent_name && (
                        <div>
                            <div className="text-xs text-[#7B61FF] uppercase tracking-wider font-data font-bold mb-2">Agent Profile</div>
                            <div className="bg-[#18181B] border border-[#F0EFF4]/10 rounded-xl p-4 space-y-2 text-sm">
                                {[
                                    { label: 'Name',       val: intake.agent_name },
                                    { label: 'Role',       val: intake.agent_role },
                                    { label: 'Comm Style', val: intake.comm_style },
                                    { label: 'Pushback',   val: intake.pushback_level },
                                    { label: 'Channel',    val: intake.preferred_channel },
                                    { label: 'Hours',      val: intake.active_hours },
                                ].filter(x => x.val).map(({ label, val }) => (
                                    <div key={label} className="flex justify-between">
                                        <span className="text-[#F0EFF4]/40">{label}</span>
                                        <span className="text-[#F0EFF4] font-medium text-right">{val}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Goals & Priorities */}
                    {(intake.top_problem || intake.success_30_days || intake.success_90_days || intake.could_replace) && (
                        <div>
                            <div className="text-xs text-[#F0EFF4]/40 uppercase tracking-wider font-data mb-2">Goals & Priorities</div>
                            <div className="bg-[#18181B] border border-[#F0EFF4]/10 rounded-xl p-4 space-y-3 text-sm">
                                {intake.top_problem && (
                                    <div>
                                        <span className="text-[#F0EFF4]/40 text-xs">Top Problem</span>
                                        <p className="text-[#F0EFF4]/80 mt-0.5">{intake.top_problem}</p>
                                    </div>
                                )}
                                {intake.could_replace && (
                                    <div>
                                        <span className="text-[#F0EFF4]/40 text-xs">Jobs Agent Could Replace</span>
                                        <p className="text-[#F0EFF4]/80 mt-0.5">{intake.could_replace}</p>
                                    </div>
                                )}
                                {intake.success_30_days && (
                                    <div>
                                        <span className="text-[#7B61FF] text-xs font-bold">30-Day Success</span>
                                        <p className="text-[#F0EFF4]/80 mt-0.5">{intake.success_30_days}</p>
                                    </div>
                                )}
                                {intake.success_90_days && (
                                    <div>
                                        <span className="text-[#7B61FF] text-xs font-bold">90-Day Success</span>
                                        <p className="text-[#F0EFF4]/80 mt-0.5">{intake.success_90_days}</p>
                                    </div>
                                )}
                                {(intake.priority_1 || intake.priority_2 || intake.priority_3) && (
                                    <div>
                                        <span className="text-[#F0EFF4]/40 text-xs">Priorities</span>
                                        <ol className="text-[#F0EFF4]/80 mt-0.5 space-y-0.5">
                                            {intake.priority_1 && <li className="flex gap-2"><span className="text-[#7B61FF] font-bold">1.</span> {intake.priority_1}</li>}
                                            {intake.priority_2 && <li className="flex gap-2"><span className="text-[#7B61FF] font-bold">2.</span> {intake.priority_2}</li>}
                                            {intake.priority_3 && <li className="flex gap-2"><span className="text-[#7B61FF] font-bold">3.</span> {intake.priority_3}</li>}
                                        </ol>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Quick Actions */}
                    <div>
                        <div className="text-xs text-[#F0EFF4]/40 uppercase tracking-wider font-data mb-2">Quick Actions</div>
                        <div className="grid grid-cols-2 gap-2">
                            <a
                                href={portalUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center justify-center gap-1.5 px-3 py-2 bg-[#F0EFF4]/5 hover:bg-[#7B61FF]/20 border border-[#F0EFF4]/10 rounded-lg text-xs text-[#F0EFF4] hover:text-[#7B61FF] transition-all font-semibold"
                            >
                                <ExternalLink className="w-3.5 h-3.5" />
                                Open Portal
                            </a>
                            <button
                                onClick={copyPortalUrl}
                                className="flex items-center justify-center gap-1.5 px-3 py-2 bg-[#F0EFF4]/5 hover:bg-[#F0EFF4]/10 border border-[#F0EFF4]/10 rounded-lg text-xs text-[#F0EFF4]/70 hover:text-[#F0EFF4] transition-all font-semibold"
                            >
                                <Copy className="w-3.5 h-3.5" />
                                Copy URL
                            </button>
                            <Link
                                to={`/admin/client/${client.id}`}
                                onClick={onClose}
                                className="flex items-center justify-center gap-1.5 px-3 py-2 bg-[#7B61FF]/10 hover:bg-[#7B61FF]/20 border border-[#7B61FF]/30 rounded-lg text-xs text-[#7B61FF] transition-all font-semibold col-span-2"
                            >
                                <ChevronRight className="w-3.5 h-3.5" />
                                Open Full View
                            </Link>
                        </div>
                    </div>

                    {/* Recent Activity */}
                    <div>
                        <div className="flex items-center gap-2 mb-2">
                            <Clock className="w-3.5 h-3.5 text-[#F0EFF4]/40" />
                            <span className="text-xs text-[#F0EFF4]/40 uppercase tracking-wider font-data">Recent Activity</span>
                        </div>

                        {recentUpdates.length > 0 ? (
                            <div className="space-y-2">
                                {recentUpdates.map((update, i) => (
                                    <div key={i} className="flex gap-3 text-sm py-2 px-3 bg-[#18181B] rounded-lg border border-[#F0EFF4]/5">
                                        <span className="shrink-0">{UPDATE_ICONS[update.type] || 'ℹ️'}</span>
                                        <div className="flex-1 min-w-0">
                                            <span className="text-[#F0EFF4]/80">{update.message}</span>
                                            {update.link_url && (
                                                <a href={update.link_url} className="text-[#7B61FF] ml-2 font-medium hover:underline text-xs" target="_blank" rel="noreferrer">
                                                    {update.link_label || 'Link'}
                                                </a>
                                            )}
                                        </div>
                                        <span className="text-xs text-[#F0EFF4]/30 shrink-0">{new Date(update.created_at).toLocaleDateString()}</span>
                                    </div>
                                ))}
                                {(client.client_updates || []).length > 3 && (
                                    <Link
                                        to={`/admin/client/${client.id}`}
                                        className="block text-center text-xs text-[#7B61FF] hover:underline py-1"
                                    >
                                        View all {client.client_updates.length} updates →
                                    </Link>
                                )}
                            </div>
                        ) : (
                            <p className="text-sm text-[#F0EFF4]/30 italic">No activity yet</p>
                        )}
                    </div>

                    {/* Quick Post Update */}
                    <div className="border-t border-[#F0EFF4]/10 pt-5">
                        <div className="flex items-center gap-2 mb-3">
                            <MessageSquare className="w-3.5 h-3.5 text-[#7B61FF]" />
                            <span className="text-xs text-[#F0EFF4]/40 uppercase tracking-wider font-data">Post Update</span>
                        </div>
                        <form onSubmit={handleSubmitUpdate} className="space-y-3">
                            <input
                                required
                                name="message"
                                type="text"
                                placeholder="Quick update…"
                                className="w-full bg-[#18181B] border border-[#F0EFF4]/10 rounded-lg px-3 py-2 text-sm text-[#F0EFF4] placeholder:text-[#F0EFF4]/30 focus:border-[#7B61FF] focus:outline-none transition-colors"
                            />
                            <input type="hidden" name="type" value="info" />
                            <input type="hidden" name="link_url" value="" />
                            <input type="hidden" name="link_label" value="" />
                            <div className="flex justify-end">
                                <button
                                    type="submit"
                                    className="flex items-center gap-1.5 px-3 py-1.5 bg-[#7B61FF] hover:bg-[#6b52e8] text-white text-xs font-bold rounded-lg transition-colors"
                                >
                                    <Send className="w-3 h-3" /> Post
                                </button>
                            </div>
                        </form>
                    </div>

                </div>
            </div>
        </div>
    );
};

export default ClientDrawer;
